import { divRound, fromUnits, pow10, toUnits } from './decimal';

/**
 * Live document totals for purchase orders and supplier bills — a faithful client copy of the backend
 * totals engine (ACCOUNTING_RULES §1.6, §3 "Tax" and "Discounts"):
 *
 * - gross = round(qty × unit price) (or a given gross, e.g. a bill line at its receipt value);
 * - the line discount (amount or %) comes off the gross;
 * - a document discount is prorated over the discounted lines by the largest-remainder method so the
 *   shares add up exactly (ties go to the earliest line);
 * - tax per line: exclusive `tax = round(net × rate)`; inclusive `net = round(amount / (1 + rate))`,
 *   `tax = amount − net`;
 * - every amount is rounded at line level (ROUND_HALF_UP); document totals are plain sums.
 *
 * Everything is exact BigInt arithmetic on decimal strings. Invalid input never throws: the line or
 * document gets an `error` (the server refuses it on save) and the discount is capped for the preview.
 */

export type DiscountType = 'AMOUNT' | 'PERCENT';

export interface Discount {
  type: DiscountType;
  value: string;
}

export interface TotalsLine {
  /** Quantity in the entered unit. */
  qty: string;
  /** Price per entered unit. */
  unitPrice: string;
  /** Gross given directly instead of qty × price (a bill line at its receipt value). */
  gross?: string;
  discount?: Discount | null;
  /** Percent, e.g. `17` for 17 %. Ignored when tax is disabled. */
  taxRate?: string | null;
}

export interface TotalsOptions {
  /** Currency minor units (2 for PKR). */
  decimals: number;
  taxEnabled: boolean;
  pricesIncludeTax: boolean;
  documentDiscount?: Discount | null;
}

export interface LineTotals {
  gross: string;
  lineDiscount: string;
  documentDiscount: string;
  /** Amount before tax. */
  net: string;
  taxRate: string;
  tax: string;
  /** net + tax. */
  total: string;
  error?: string;
}

export interface DocumentTotals {
  lines: LineTotals[];
  /** Σ gross. */
  subtotal: string;
  documentDiscount: string;
  /** Σ line discounts + document discount. */
  discountTotal: string;
  netTotal: string;
  taxTotal: string;
  total: string;
  /** Problem with the document discount, if any. */
  error?: string;
}

/** Quantities, prices and rates carry at most 4 decimals in the API. */
const IN = 4;
const HUNDRED_AT_IN = 100n * pow10(IN);

function discountAmount(
  discount: Discount | null | undefined,
  base: bigint,
  decimals: number,
): { amount: bigint; error?: string } {
  if (!discount || discount.value.trim() === '') return { amount: 0n };
  const value = toUnits(discount.value, IN);
  if (value < 0n) return { amount: 0n, error: 'A discount cannot be negative.' };
  let amount: bigint;
  if (discount.type === 'PERCENT') {
    if (value > HUNDRED_AT_IN)
      return { amount: base, error: 'A percentage discount is at most 100.' };
    amount = divRound(base * value, HUNDRED_AT_IN);
  } else {
    amount = toUnits(discount.value, decimals);
  }
  if (amount > base) return { amount: base, error: 'The discount is larger than the amount.' };
  return { amount };
}

/** Splits `amount` over `weights` in proportion so the parts sum exactly (largest remainder). */
export function prorateUnits(amount: bigint, weights: bigint[]): bigint[] {
  const total = weights.reduce((a, b) => a + b, 0n);
  if (total === 0n || amount === 0n) return weights.map(() => 0n);
  const floored = weights.map((w) => (amount * w) / total);
  const remainders = weights.map((w, i) => ({ i, r: (amount * w) % total }));
  let left = amount - floored.reduce((a, b) => a + b, 0n);
  remainders.sort((a, b) => (b.r > a.r ? 1 : b.r < a.r ? -1 : a.i - b.i));
  const out = [...floored];
  for (const { i } of remainders) {
    if (left <= 0n) break;
    out[i] = (out[i] ?? 0n) + 1n;
    left -= 1n;
  }
  return out;
}

/** Decimal-string version of {@link prorateUnits} at the currency's decimals. */
export function prorate(amount: string, weights: string[], decimals: number): string[] {
  return prorateUnits(
    toUnits(amount, decimals),
    weights.map((w) => toUnits(w, decimals)),
  ).map((u) => fromUnits(u, decimals));
}

export function computeTotals(lines: TotalsLine[], options: TotalsOptions): DocumentTotals {
  const d = options.decimals;
  const fmt = (u: bigint) => fromUnits(u, d);

  const first = lines.map((line) => {
    const errors: string[] = [];
    let gross: bigint;
    if (line.gross !== undefined) gross = toUnits(line.gross, d);
    else {
      const q = toUnits(line.qty, IN);
      const p = toUnits(line.unitPrice, IN);
      if (q < 0n) errors.push('Quantity cannot be negative.');
      if (p < 0n) errors.push('A price cannot be negative.');
      // qty (4 dp) × price (4 dp) = 8 dp → currency decimals
      gross = 2 * IN >= d ? divRound(q * p, pow10(2 * IN - d)) : q * p * pow10(d - 2 * IN);
    }
    const disc = discountAmount(line.discount, gross < 0n ? 0n : gross, d);
    if (disc.error) errors.push(disc.error);
    return { line, gross, lineDiscount: disc.amount, afterLine: gross - disc.amount, errors };
  });

  const base = first.reduce((a, l) => a + (l.afterLine > 0n ? l.afterLine : 0n), 0n);
  const doc = discountAmount(options.documentDiscount, base, d);
  const shares = prorateUnits(
    doc.amount,
    first.map((l) => (l.afterLine > 0n ? l.afterLine : 0n)),
  );

  const result = first.map((l, i): LineTotals => {
    const share = shares[i] ?? 0n;
    const amount = l.afterLine - share;
    const rate = options.taxEnabled ? toUnits(l.line.taxRate ?? '0', IN) : 0n;
    let net: bigint;
    let tax: bigint;
    if (options.pricesIncludeTax) {
      net = divRound(amount * HUNDRED_AT_IN, HUNDRED_AT_IN + rate);
      tax = amount - net;
    } else {
      net = amount;
      tax = divRound(net * rate, HUNDRED_AT_IN);
    }
    return {
      gross: fmt(l.gross),
      lineDiscount: fmt(l.lineDiscount),
      documentDiscount: fmt(share),
      net: fmt(net),
      taxRate: fromUnits(rate, IN),
      tax: fmt(tax),
      total: fmt(net + tax),
      ...(l.errors.length ? { error: l.errors.join(' ') } : {}),
    };
  });

  const sum = (pick: (l: LineTotals) => string) =>
    fmt(result.reduce((a, l) => a + toUnits(pick(l), d), 0n));

  return {
    lines: result,
    subtotal: sum((l) => l.gross),
    documentDiscount: fmt(doc.amount),
    discountTotal: fmt(
      result.reduce((a, l) => a + toUnits(l.lineDiscount, d) + toUnits(l.documentDiscount, d), 0n),
    ),
    netTotal: sum((l) => l.net),
    taxTotal: sum((l) => l.tax),
    total: sum((l) => l.total),
    ...(doc.error ? { error: doc.error } : {}),
  };
}

/** Base quantities may carry up to 10 decimals (4 dp qty × 6 dp unit factor). */
const BASE = 10;

/** Quantity in base units: qty × factor. */
export function toBase(qty: string, factor: string): string {
  return fromUnits(divRound(toUnits(qty, IN) * toUnits(factor, 6), pow10(IN + 6 - BASE)), BASE);
}

/**
 * The receipt value (GRNI) a bill line clears when billing `qtyBase` more of a receipt line, spread by
 * cumulative quantity so partial bills clear the receipt value exactly:
 * `value(q) = round(totalCost × q / accepted)`, `grni = value(before + qty) − value(before)`.
 */
export function receiptValue(input: {
  totalCost: string;
  qtyAccepted: string;
  /** Base units already billed (or returned before billing). */
  qtyBefore: string;
  qtyBase: string;
  decimals: number;
}): string {
  const d = input.decimals;
  const accepted = toUnits(input.qtyAccepted, BASE);
  if (accepted === 0n) return fromUnits(0n, d);
  const cost = toUnits(input.totalCost, d);
  const before = toUnits(input.qtyBefore, BASE);
  const value = (q: bigint) => divRound(cost * q, accepted);
  return fromUnits(value(before + toUnits(input.qtyBase, BASE)) - value(before), d);
}
